<template>
  <VCol cols="12">
    <SettingsCard
      :title="$t('title')"
      :subtitle="$t('subtitle')"
    >
      <div class="notifications-settings__content">
        <div class="account-settings__field-row account-settings__field-row--switch">
        <div class="account-settings__field-row-header account-settings__field-row-header--has-switch">
          <VSwitch
            :input-value="receiveEmailNotifications"
            hide-details
            inset
            :loading="isSaving"
            :disabled="isLoading"
            @change="onToggleEmail"
          />

          <div class="account-settings__field-label-wrapper">
            <div class="account-settings__field-label">
              {{ $t('emailLabel') }}
            </div>
          </div>

          <button
            class="account-settings__how-it-works"
            type="button"
            @click="openHowItWorks = !openHowItWorks"
          >
            <span>{{ $t('howItWorks') }}</span>
            <img
              :src="chevronIcon"
              alt=""
              class="account-settings__how-it-works-chevron"
              :class="{ 'account-settings__how-it-works-chevron--open': openHowItWorks }"
            />
          </button>
        </div>

        <transition name="account-settings-expand">
          <div
            v-if="openHowItWorks"
            class="account-settings__field-description account-settings__field-description--expanded"
          >
            {{ $t('howItWorksText') }}
          </div>
        </transition>
        </div>
      </div>
    </SettingsCard>
  </VCol>
</template>

<script>
import {
  onMounted, ref,
} from '@vue/composition-api';
import SettingsCard from '@/components/Settings/SettingsCard.vue';
import useSystemNotifications from '@/compositions/useSystemNotifications';

export default {
  name: 'AccountSettingsNotifications',
  components: {
    SettingsCard,
  },
  setup() {
    const {
      settings,
      fetchSettings,
      updateSettings,
    } = useSystemNotifications();

    const receiveEmailNotifications = ref(false);
    const openHowItWorks = ref(false);
    const isLoading = ref(false);
    const isSaving = ref(false);

    onMounted(async () => {
      isLoading.value = true;
      try {
        await fetchSettings();
        receiveEmailNotifications.value = settings.value?.receiveEmailNotifications ?? false;
      } finally {
        isLoading.value = false;
      }
    });

    async function onToggleEmail(value) {
      const previousValue = receiveEmailNotifications.value;
      receiveEmailNotifications.value = value;
      isSaving.value = true;

      try {
        await updateSettings({ receiveEmailNotifications: value });
      } catch (e) {
        receiveEmailNotifications.value = previousValue;
        throw e;
      } finally {
        isSaving.value = false;
      }
    }

    return {
      receiveEmailNotifications,
      openHowItWorks,
      isLoading,
      isSaving,
      onToggleEmail,
    };
  },
  computed: {
    chevronIcon() {
      return require('@/assets/img/account/settings/smallChevron.svg');
    },
  },
};
</script>

<style lang="scss" scoped>
.notifications-settings__content {
  margin-top: 12px;
}

.account-settings__field-row {
  width: 100%;
}

.account-settings__field-row-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.account-settings__field-row-header--has-switch {
  align-items: flex-start;
}

.account-settings__field-row-header--has-switch::v-deep {
  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }
  .v-messages {
    display: none;
  }
}

.account-settings__field-label-wrapper {
  flex: 1 1 auto;
  padding-top: 6px;
}

.account-settings__field-label {
  color: #212121;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.account-settings__how-it-works {
  display: inline-flex;
  align-items: center;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: auto;
  color: rgba(112, 112, 112, 1);
  font-size: 12px;
  font-weight: 400;
  line-height: 120%;
  white-space: nowrap;

  span {
    margin-right: 4px;
  }
}

.account-settings__how-it-works-chevron {
  display: block;
  transition: transform 0.25s ease;
}

.account-settings__how-it-works-chevron--open {
  transform: rotate(180deg);
}

.account-settings__field-description {
  margin-top: 4px;
  color: #707070;
  font-size: 12px;
  font-weight: 400;
  line-height: 140%;
}

.account-settings__field-description--expanded {
  margin-top: 14px;
}

.account-settings-expand-enter-active,
.account-settings-expand-leave-active {
  transition: opacity 0.25s ease, max-height 0.3s ease, margin-top 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.account-settings-expand-enter,
.account-settings-expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0 !important;
}
</style>

<i18n locale="en" lang="yaml">
  title: Notification settings
  subtitle: Configure how you want to receive system notifications.
  emailLabel: Receive notifications by email
  howItWorks: How it works
  howItWorksText: When enabled, important events and updates will be duplicated to your email. You can change this setting anytime.
</i18n>

<i18n locale="ru" lang="yaml">
  title: Настройки уведомлений
  subtitle: Управляйте тем, как вы хотите получать уведомления от системы.
  emailLabel: Получать уведомления на Email
  howItWorks: Как это работает
  howItWorksText: Если включено, важные события и обновления будут дополнительно приходить на вашу почту. Настройку можно изменить в любой момент.
</i18n>
